// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

/**
 * Data-residency region display labels (#1668).
 *
 * Maps an Azure region name (as resolved by the backend `Residency:CustomerRegions`
 * config and surfaced on tenant DTOs as `residencyRegion`) to a short badge label and
 * a fuller name for tooltips. This is the *storage* concept — where a tenant's data
 * physically lives — and is deliberately separate from the tenant's compliance
 * Country (jurisdiction, #1468).
 *
 * Unknown/unmapped regions fall back to the raw region string so a newly-onboarded
 * region still renders something meaningful without a code change.
 */

export interface ResidencyLabel {
  /** Compact badge text, e.g. "EU", "SA". */
  short: string;
  /** Fuller name for tooltips, e.g. "North Europe", "South Africa". */
  full: string;
}

const REGION_LABELS: Record<string, ResidencyLabel> = {
  northeurope: { short: 'EU', full: 'North Europe' },
  southafricanorth: { short: 'SA', full: 'South Africa' },
  westeurope: { short: 'EU', full: 'West Europe' },
  uksouth: { short: 'UK', full: 'UK South' },
  eastus: { short: 'US', full: 'East US' },
};

/**
 * Resolve a region name to its display label. Returns `null` when no region is
 * provided so callers can render nothing (graceful — e.g. older API responses or
 * tests that omit `residencyRegion`).
 */
export function residencyLabel(region: string | null | undefined): ResidencyLabel | null {
  if (!region || !region.trim()) return null;
  const key = region.trim().toLowerCase();
  return REGION_LABELS[key] ?? { short: region, full: region };
}
